/**
 * AVBP L2B intake — the data layer for the client-facing intake form
 * (Step-2 report-fidelity rebuild, Slice 1).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS FILE EXISTS — the write contract is the whole point of Slice 1.
 * ─────────────────────────────────────────────────────────────────────────
 *  The composer engine reads `clients/{clientId}/intake/{tier}` and consumes
 *  the field set under `answers.*` DIRECTLY — there is NO translation layer
 *  (per AVBP_L2B_R1_INTAKE_CONTRACT_2026-06-06, §B). This module owns the
 *  ONLY place the portal turns form state into that on-disk shape, so the
 *  shape is unit-tested in one spot and can never silently drift.
 *
 *  Tier doc id = `L2B_R1` (OQ-A locked: two tiers L2B_R1 / L2B_R2).
 *  Envelope: { schema_version:'L2B/v2', tier:'L2B_R1', answers:{…}, updated_at }
 *
 *  Slice 1 = CLIENT-OWNED fields ONLY. The staff-technical fields
 *  (north / boundary / entrance / AUO) are Slices 2–4 and are deliberately
 *  NOT written here. `merge:true` on the write lets a later staff hybrid-fill
 *  add those technical fields to the SAME doc without clobbering this slice.
 *
 *  North bearing is NOT in this form — it lives on the client ROOT doc at
 *  `profile.north_rotation` (staff, Slice 2). We never touch it here.
 *
 *  The pure functions (`buildIntakeAnswers`, `buildIntakeEnvelope`,
 *  `formFromAnswers`, `blankIntakeForm`, the small helpers) carry NO Firebase
 *  import so the Node test runner can exercise them via `_tsload.cjs`. The
 *  impure `loadIntake` / `saveIntake` sit at the bottom and import `db`.
 * ─────────────────────────────────────────────────────────────────────────
 */

import {
  type DesignProgrammeInput,
  blankDesignProgramme,
  programmeToAnswer,
  programmeFromAnswer,
  programmeHasContent,
} from './designProgramme';
import {
  type ConstructionDetailsInput,
  blankConstructionDetails,
  constructionToAnswer,
  constructionFromAnswer,
  constructionHasContent,
} from './constructionDetails';
import {
  type DesignPlansPrefsInput,
  blankDesignPlansPrefs,
  plansPrefsToAnswer,
  plansPrefsFromAnswer,
  plansPrefsHasContent,
} from './designPlansPrefs';

/** The locked tier doc id this slice writes (OQ-A). */
export const L2B_R1_TIER = 'L2B_R1' as const;
/** The locked schema tag the engine fixture uses. */
export const INTAKE_SCHEMA_VERSION = 'L2B/v2' as const;

/** The project-type enum the engine + cover label read (contract UNI-1). */
export const PROJECT_TYPES = [
  'residence',
  'office',
  'commercial',
  'retail',
  'industrial',
  'institutional',
  'hospitality',
  'healthcare',
  'mixed_use',
] as const;
export type ProjectType = (typeof PROJECT_TYPES)[number];

/**
 * L1A redesign (2026-06-21) — INTENDED USE. The 9 uses-of-space a client may be
 * selecting a property FOR. Identical set to PROJECT_TYPES (a property's "use of
 * space" === an intended use), held as its own named export so the L1A "what are
 * you selecting a property for?" mandate reads clearly. Drives (a) the concern
 * palette loaded in §3 and (b) the People relation/chart mode (residence/mixed →
 * family vocab; the other uses → work-role / org).
 */
export const INTENDED_USES = PROJECT_TYPES;
export type IntendedUse = ProjectType;

/**
 * L1A redesign — does an intended use put the People module in FAMILY mode
 * (relation-to-client vocab + household chart) vs WORK mode (work-role vocab +
 * org chart)? residence + mixed_use → family; every other use → work. The empty
 * (unselected) state defaults to family so a fresh form shows the family vocab.
 */
export function intendedUseIsFamily(use: string): boolean {
  return use === 'residence' || use === 'mixed_use' || use === '';
}

/** Report language (contract R1-S3). */
export const LANGUAGES = ['en', 'hi'] as const;
export type Language = (typeof LANGUAGES)[number];

/** Per-concern severity (optional, contract UNI-5). */
export const SEVERITIES = ['Mild', 'Moderate', 'Severe', 'Critical'] as const;
export type Severity = (typeof SEVERITIES)[number];

/* ─────────────────────────── form-state types ─────────────────────────── */

/**
 * A presenting concern as the FORM holds it. `text` is the human label;
 * `severity` is optional. `concern_id` is an optional stable id for the
 * engine's footnote join (step8) — the form leaves it blank for a free-typed
 * concern and the engine tolerates plain text.
 */
export interface ConcernInput {
  text: string;
  severity?: Severity | '';
  concern_id?: string;
  /**
   * L1A redesign (2026-06-21) — TWO ASPECTS per area. When the client ticks an
   * area they mark whether it is a problem they're FACING (`issue`), an
   * ASPIRATION they hold (`aspiration`), or both. Default false/false on a row
   * carried from a pre-redesign doc (the report tolerates absent === unmarked).
   */
  issue?: boolean;
  aspiration?: boolean;
  /** Optional verbatim note (client-verbatim, preserved as typed — AD-006). */
  note?: string;
  /** Optional "this concern applies to a specific person" pointer (kept open). */
  applies_to_person?: string;
}

/**
 * An occupant as the FORM holds it. Birth data is OPTIONAL at the field level
 * (OQ-2). A person missing complete birth data is saved WITHOUT it (no error)
 * — the engine silently skips charting them.
 */
export interface PersonInput {
  id: string;
  name: string;
  /**
   * Axis 1 (AD-027) — participant_type: co_owner / family_member / tenant /
   * external_professional (Vastu zones + visibility + legal). The form field is
   * historically named `role`; a legacy free-text value still loads + displays.
   */
  role: string;
  /**
   * Axis 2 (AD-027) — relation_to_client: the SPECIFIC familial/business relation
   * to the primary client/native (relational astrology). OPTIONAL controlled
   * vocab (see RELATION_TO_CLIENT_VALUES). Captured here; compute is DEFERRED.
   */
  relation_to_client?: string;
  /**
   * Axis 3a (AD-027) — work_role: designation in the organisation. OPTIONAL
   * controlled vocab (see WORK_ROLE_VALUES). Captured here; org-compute DEFERRED.
   */
  work_role?: string;
  /**
   * Axis 3b (AD-027) — reports_to: the stable row `id` of ANOTHER person in this
   * same intake that this person reports to (the org-chart edge). OPTIONAL.
   */
  reports_to?: string;
  /**
   * Axis 3c (AD-027) — department: functional department. OPTIONAL controlled
   * vocab (see DEPARTMENT_VALUES).
   */
  department?: string;
  dob?: string;
  time_of_birth?: string;
  place_of_birth?: string;
  /**
   * L1A redesign (2026-06-21) — structured place of birth. `place_of_birth` is
   * kept as the human label (back-compat + engine), but the city/state/country
   * are captured separately for precision (city + country required when astro is
   * on). `pob_city` etc. are merged into `place_of_birth` on write when present.
   */
  pob_city?: string;
  pob_state?: string;
  pob_country?: string;
  /** Birth-place latitude (string in the form; coerced on write). */
  lat?: string;
  lon?: string;
  /** IANA tz, e.g. Asia/Kolkata — needed for the LOCKED no-DST policy. */
  tz?: string;
  /**
   * L1A redesign — "bread-earner of the family" tick. Optional flag; the report
   * may weight the primary earner's chart. Default false.
   */
  is_breadwinner?: boolean;
  /**
   * L1A redesign — per-person astrology toggle. Default true (on). When off, the
   * birth fields become optional and the person is saved without a chart. (The
   * legacy project-level `astro_in_scope` is retained for back-compat / other
   * tiers; the L1A People module reads/writes this per-person flag.)
   */
  astro_in_scope?: boolean;
  /**
   * STANDARD per-person Health module (2026-06-23). Optional free-text health
   * concern for THIS person, read alongside their astrology — NOT a property-
   * level section. Appears on every astrology-bearing template (L1A, L1B, L2B,
   * L3A/B, Astrology). Free text now; may extend to quick-pick chips later, so
   * the field is intentionally a simple string. Never diagnose/prescribe — this
   * is a context note the reading keeps in view.
   */
  health_concerns?: string;
}

// ─── Module 4 (Phase 3) — Property entity linkage types ──────────────────

/**
 * A reference to a Property entity from an intake answers doc.
 * `role` mirrors the `PropertyRef.role` on the project doc:
 *   'primary'   — the single property being reported on (L2B, L3B, etc.)
 *   'candidate' — one of several properties under consideration (L1A multi-prop)
 */
export interface IntakePropertyRef {
  propertyId: string;
  role: 'primary' | 'candidate';
  label?: string;
}

/**
 * Fields added to `IntakeAnswers` by Module 4 (Phase 3). These are additive —
 * existing intake shapes remain valid; these keys are optional.
 *
 * These fields live on the intake `answers` map (same doc as the client-owned
 * and staff-technical slices) and are written via `savePropertyLink`.
 */
export interface IntakeAnswers {
  /** FK to `properties/{propertyId}` — set when this intake is linked to a Property entity. */
  propertyId?: string;
  /** FK to `properties/{pid}/grids/{gridId}` — the active grid at intake time. */
  gridId?: string;
  /** For L1A multi-property: all candidate property refs. */
  propertyRefs?: IntakePropertyRef[];
}

/* ───────────────────── L1A redesign — property details ───────────────────── */

/**
 * One uploaded media item (photo/video) on a candidate property, tagged with the
 * Floor + Place/room it belongs to (so evaluation can filter media floor-wise and
 * room-wise). `url` is the Storage download URL (or a local object-URL before
 * upload completes); `type` distinguishes image vs video for the thumbnail.
 */
export interface MediaInput {
  /** Stable row id (form-local). */
  id: string;
  url: string;
  type: 'image' | 'video';
  /** mime type of the original file (image/jpeg, video/mp4, …) when known. */
  mimeType?: string;
  /** Object-URL of a generated thumbnail (esp. for video) — preview only. */
  thumbUrl?: string;
  /** Floor tag — a free string (combobox; the floor labels feed the options). */
  floor: string;
  /** Place / room tag — a free string (combobox, type-to-pick-or-add). */
  place: string;
  /** Storage path (when uploaded). */
  storagePath?: string;
}

/** One floor on a candidate property: a floor label, its area, and its plan. */
export interface FloorInput {
  /** Stable row id (form-local). */
  id: string;
  /** Floor label — canonical FLOOR_LABELS value (e.g. "Ground Floor") or custom. */
  floor: string;
  /** Approx area for this floor (sq ft, string in the form). */
  areaApprox?: string;
  /** Uploaded plan download URL (PDF/img), when present. */
  planUrl?: string;
  planStoragePath?: string;
  /** Original plan file name — so the UI shows WHAT was attached, not just a tick. */
  planName?: string;
  /** Plan mime type (`application/pdf` vs `image/*`) — drives the icon/thumbnail. */
  planMime?: string;
}

/**
 * One candidate property the client is comparing (L1A is multi-property — up to
 * 3). Carries its own use, site location, areas, floors, and media. Writes
 * through to the Property entity (`properties/{P#####}`) where it exists; else
 * stays on the intake doc (the staging-area pattern).
 */
export interface PropertyInput {
  /** Stable row id (form-local). */
  id: string;
  /** Property name / label (e.g. "Plot near Ring Road"). */
  label: string;
  /** Use of this space — defaults to the form's intended_use, overridable. */
  use: ProjectType | '';
  /** Site location & north (reuses the working SiteLocation parse layer). */
  google_maps_url: string;
  /** The single combined "lat, long" coordinate field (decimal OR DMS). */
  coordPair: string;
  /** True-north bearing in degrees (string in the form), optional. */
  northRotation?: string;
  /** Google Earth north-screenshot download URL, optional. */
  northScreenshotUrl?: string;
  /** Approx built-up area (sq ft, string). Drives the quote + grid. */
  builtUpAreaSqft?: string;
  /** Plot / land area (sq ft, string), optional. */
  plotAreaSqft?: string;
  /** Floors in this property (each: label + area + plan). */
  floors: FloorInput[];
  /** Photos & videos, each tagged with floor + place/room. */
  media: MediaInput[];
  /** Linked Property entity id, when this candidate is backed by a property doc. */
  propertyId?: string;
  /**
   * L1B drawing gate (2026-06-23) — does the client have a floor plan/drawing of
   * this occupied space? A measured plan is what lets us trace the boundary &
   * grid; photos alone can't. 'yes' → per-floor plan upload; 'no' → surface the
   * Survey & Map-Making add-on / measured hand-sketch path. Only used by the L1B
   * single-property drawing-gate mode; absent on L1A multi-property candidates.
   */
  hasDrawing?: 'yes' | 'no';
}

/** The full client-owned intake form state (Slice-1 scope). */
export interface IntakeForm {
  client_name: string;
  firm: string;
  property_address: string;
  project_type: ProjectType | '';
  country: string;
  scope_label: string;
  language: Language;
  presenting_concerns: ConcernInput[];
  goals: string[];
  /** Project-level astro toggle (OQ-2). Default false. */
  astro_in_scope: boolean;
  persons: PersonInput[];
  /** Remedy implementation start (optional, contract R1-S3). */
  follow_up_start_date: string;
  /* ── L1A redesign (2026-06-21) — additive client-owned fields ── */
  /** Contact email (required at L1A; auto-fetched from the person record). */
  email: string;
  /** Contact WhatsApp number (required at L1A; auto-fetched). */
  whatsapp: string;
  /** The person's own contact/home address (optional; NOT the property address). */
  address: string;
  /**
   * Intended use — what the client is selecting a property FOR (L1A mandate).
   * Drives the concern palette + People relation/chart mode. One of INTENDED_USES.
   */
  intended_use: ProjectType | '';
  /** Candidate properties being compared (L1A multi-property; max 3 in the UI). */
  properties: PropertyInput[];
  /**
   * L1C / L2C (2026-06-23 v2) — the architect's drawings the client uploads for a
   * plan review (L1C) or integration coordination (L2C). The PROPERTY (site
   * location, true-north, area) is common to every drawing version; L2C adds
   * revised versions over time, L1C is a single set. Owned by the `drawings_upload`
   * REAL module. Empty default on tiers that don't capture architect drawings.
   */
  drawings: DrawingsInput;
  /* ── L1B (2026-06-23) — Plans & preferences (Box 7) ── */
  /** "What have you already tried" — verbatim free text (optional). */
  prior_remedies_tried: string;
  /**
   * Four willingness questions, each a single-pick. Stored as a flat map keyed by
   * the question id → the chosen option label. Required ones (implement, discuss,
   * follow_process) are enforced at the L1B final submit; structural is optional.
   * Kept as a string→string map so adding/renaming an option needs no type change.
   */
  willingness: WillingnessAnswers;
  /**
   * AD-040 (2026-06-24) — the structured Design Programme (A→H). Replaces the
   * old single-textarea `design_programme` scaffold. Owned by the now-REAL
   * `design_programme` module; persisted as `answers.designProgramme`. Wired
   * into every tier that selects `design_programme` (L2C/L2A/L2D/L3A/L3B).
   */
  designProgramme: DesignProgrammeInput;
  /**
   * AD-042 (2026-06-25) — L2D structured Construction details (11 groups).
   * Owned by the now-REAL `construction_details` module; `answers.constructionDetails`.
   */
  constructionDetails: ConstructionDetailsInput;
  /**
   * AD-042 — L3B "Plans & preferences" (design-path decision + preferences,
   * 6 questions). Owned by `l3b_plans_preferences`; `answers.designPlansPrefs`.
   */
  designPlansPrefs: DesignPlansPrefsInput;
}

/** L1B willingness single-pick answers (Box 7). All optional in the type; the
 * required subset is enforced by the L1B validator at submit. */
export interface WillingnessAnswers {
  /** Willingness to implement the remedies (REQUIRED at L1B). */
  implement?: string;
  /** Openness to structural change (OPTIONAL — "rarest case, if at all"). */
  structural?: string;
  /** Willingness to discuss & give us time (REQUIRED at L1B). */
  discuss?: string;
  /** Willingness to follow a proven process (REQUIRED at L1B). */
  follow_process?: string;
}

/* ───────────────── L1C / L2C drawings (2026-06-23 v2) ───────────────── */

/** The architect-drawing stage on a version (concept → working). */
export type DrawingStage = 'concept' | 'schematic' | 'developed' | 'working' | 'other';

/** Canonical drawing-stage options (value + label) for the version selector. */
export const DRAWING_STAGES: { value: DrawingStage; label: string }[] = [
  { value: 'concept', label: 'Concept' },
  { value: 'schematic', label: 'Schematic' },
  { value: 'developed', label: 'Developed' },
  { value: 'working', label: 'Working drawings' },
  { value: 'other', label: 'Other' },
];

/**
 * One revised drawing version (L2C adds these over time; L1C carries exactly one).
 * Maps onto the Property `floors[].plans[]` versioning (AD-013) — a version is a
 * stage + label + its own per-floor plan files. The property's location & true-
 * north (on `DrawingsInput`) stay FIXED and are shared across all versions.
 */
export interface DrawingVersion {
  /** Stable row id (form-local). */
  id: string;
  /** Drawing stage of this version. */
  stage: DrawingStage | '';
  /** Free version label / note (e.g. "Concept v2 · revised after NE change"). */
  label: string;
  /** Per-floor drawing files for THIS version (reuses FloorInput's plan upload). */
  floors: FloorInput[];
}

/**
 * The drawings-upload module's owned state. PROPERTY-level location/north/area are
 * shared across every version (the property is one site); `versions[]` holds the
 * drawing sets. L1C uses one version; L2C appends revised versions over time.
 * `architect_*` are L2C-only (the renderer hides them on L1C via the bright line).
 */
export interface DrawingsInput {
  /* ── property (common to all versions; mirrors the L1A site-location block) ── */
  google_maps_url: string;
  /** Combined "lat, long" coordinate field (decimal OR DMS; each fills the other). */
  coordPair: string;
  /** True-north bearing in degrees (string), optional — the field the owner caught. */
  northRotation?: string;
  /** Google Earth north-screenshot download URL, optional. */
  northScreenshotUrl?: string;
  /** Approx built-up area (sq ft, string), optional. */
  builtUpAreaSqft?: string;
  /** Plot / land area (sq ft, string), optional. */
  plotAreaSqft?: string;
  /**
   * AD-036.2 (2026-06-24) — Linked Property entity id (P#####), when the drawings
   * stage's property block is linked to / created in the registry. Mirrors
   * `PropertyInput.propertyId`. Set by the inline PropertySlotLink control on the
   * drawings stage (L1C/L2C); also mirrored to `answers.propertyId` for the report
   * source resolver. Absent on the client self-fill / legacy inline path.
   */
  propertyId?: string;
  /* ── the drawing version(s) ── */
  versions: DrawingVersion[];
  /* ──────────────────────────────────────────────────────────────────────
   * UNIFIED Property & drawings block (2026-06-24 FULL MERGE). The four
   * formerly-separate stub modules (property_media · space_survey ·
   * property_drawings · property_constraints) are folded IN here as the
   * "About the building" section. They describe the same building, so they
   * live with the property/drawings, not on their own tab.
   * ────────────────────────────────────────────────────────────────────── */
  /** Photos & video note (was the `property_media` stub). Optional. */
  mediaNote?: string;
  /** Measured-survey status (was the `space_survey` stub select). */
  surveyStatus?: string;
  /** Existing drawings the client already has (was the `property_drawings` stub). */
  existingDrawings?: string;
  /** Known site constraints (was the `property_constraints` stub). */
  siteConstraints?: string;
  /* ── architect & interior-designer coordination (L2C only; hidden on L1C).
   * Structured Name · Email · WhatsApp for BOTH (2026-06-24) — replaces the
   * old single optional phone/email box. On save each becomes a role-tagged
   * Contact (Architect / Interior designer) on the contact spine (AD-039).
   * `architect_contact` (legacy single phone/email) is RETAINED for back-compat
   * read of pre-merge intakes — new writes use the structured fields. ── */
  architect_name?: string;
  architect_email?: string;
  architect_whatsapp?: string;
  /** @deprecated pre-merge single phone/email box — read-only back-compat. */
  architect_contact?: string;
  interior_designer_name?: string;
  interior_designer_email?: string;
  interior_designer_whatsapp?: string;
  /** @deprecated pre-merge single interior-designer box — read-only back-compat. */
  interior_designer?: string;
  site_visit_readiness?: string;
}

/* ──────────────────────────── pure helpers ───────────────────────────── */

/** A blank form — the create-mode starting state (one empty person row). */
export function blankIntakeForm(): IntakeForm {
  return {
    client_name: '',
    firm: '',
    property_address: '',
    project_type: '',
    country: 'India',
    scope_label: '',
    language: 'en',
    presenting_concerns: [],
    goals: [],
    astro_in_scope: false,
    persons: [],
    follow_up_start_date: '',
    // L1A redesign additive defaults
    email: '',
    whatsapp: '',
    address: '',
    intended_use: '',
    properties: [],
    // L1C/L2C drawings (v2) — empty property block + no versions yet.
    drawings: blankDrawings(),
    // L1B Plans & preferences (Box 7) defaults
    prior_remedies_tried: '',
    willingness: {},
    // AD-040 — structured Design Programme (empty default).
    designProgramme: blankDesignProgramme(),
    constructionDetails: blankConstructionDetails(),
    designPlansPrefs: blankDesignPlansPrefs(),
  };
}

/** A stable-enough id for a new person/concern row (form-local only). */
export function newRowId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 8)}`;
}

/** A blank drawing version (one stage + label + its own floors). */
export function blankDrawingVersion(): DrawingVersion {
  return { id: newRowId('dv'), stage: '', label: '', floors: [] };
}

/** A blank drawings block — empty property + one starter version. */
export function blankDrawings(): DrawingsInput {
  return {
    google_maps_url: '',
    coordPair: '',
    northRotation: '',
    northScreenshotUrl: '',
    builtUpAreaSqft: '',
    plotAreaSqft: '',
    versions: [],
  };
}

/** A blank candidate-property row. `use` inherits the form's intended_use. */
export function blankProperty(use: ProjectType | '' = ''): PropertyInput {
  return {
    id: newRowId('prop'),
    label: '',
    use,
    google_maps_url: '',
    coordPair: '',
    northRotation: '',
    builtUpAreaSqft: '',
    plotAreaSqft: '',
    floors: [],
    media: [],
  };
}

/** A blank floor row. */
export function blankFloor(): FloorInput {
  return { id: newRowId('fl'), floor: '', areaApprox: '' };
}

/** A blank person row — astrology ON by default (L1A redesign). */
export function blankPerson(): PersonInput {
  return { id: newRowId('p'), name: '', role: '', astro_in_scope: true };
}

function trimOrUndef(v: unknown): string | undefined {
  const s = typeof v === 'string' ? v.trim() : '';
  return s ? s : undefined;
}

/**
 * Coerce a lat/lon string to a finite number, or undefined. We NEVER write a
 * NaN or an empty-string coordinate — the engine hard-gates charting on a
 * finite lat/lon, so a half-filled coordinate must read as "absent", not "0".
 */
function numOrUndef(v: unknown): number | undefined {
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
  const s = typeof v === 'string' ? v.trim() : '';
  if (!s) return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * PURE — map a single form person → the engine's `answers.persons[]` shape.
 * Only emits birth fields that are actually present; an incomplete person is
 * still emitted (id/name/role) — the engine skips charting them with no error
 * (OQ-2 per-person graceful skip). Returns the object the doc stores.
 */
export function personToAnswer(p: PersonInput): Record<string, unknown> {
  const out: Record<string, unknown> = {
    id: p.id,
    name: (p.name || '').trim(),
  };
  const role = trimOrUndef(p.role);
  if (role) out.role = role;
  // AD-027 Axis 2 — relation_to_client round-trips through the intake answers so
  // a returning client/staff sees it pre-filled (the Person doc is the canonical
  // store, but the intake answers must not silently drop the captured value).
  const relToClient = trimOrUndef(p.relation_to_client);
  if (relToClient) out.relation_to_client = relToClient;
  // AD-027 Axis 3 — work/reporting axis round-trips through the intake answers too.
  const workRole = trimOrUndef(p.work_role);
  if (workRole) out.work_role = workRole;
  const reportsTo = trimOrUndef(p.reports_to);
  if (reportsTo) out.reports_to = reportsTo;
  const department = trimOrUndef(p.department);
  if (department) out.department = department;
  const dob = trimOrUndef(p.dob);
  if (dob) out.dob = dob;
  const tob = trimOrUndef(p.time_of_birth);
  if (tob) out.time_of_birth = tob;
  // L1A redesign — the human place_of_birth label: prefer the explicit field,
  // else derive it from the structured city/state/country (back-compat + engine).
  const pob = placeOfBirthLabel(p);
  if (pob) out.place_of_birth = pob;
  const pobCity = trimOrUndef(p.pob_city);
  if (pobCity) out.pob_city = pobCity;
  const pobState = trimOrUndef(p.pob_state);
  if (pobState) out.pob_state = pobState;
  const pobCountry = trimOrUndef(p.pob_country);
  if (pobCountry) out.pob_country = pobCountry;
  const lat = numOrUndef(p.lat);
  if (lat !== undefined) out.lat = lat;
  const lon = numOrUndef(p.lon);
  if (lon !== undefined) out.lon = lon;
  const tz = trimOrUndef(p.tz);
  if (tz) out.tz = tz;
  if (p.is_breadwinner === true) out.is_breadwinner = true;
  // STANDARD per-person Health module (2026-06-23) — optional free-text concern,
  // read alongside the person's astrology. Emit only when the client filled it
  // (absent === none) so a person without a health note keeps the minimal shape.
  const health = trimOrUndef(p.health_concerns);
  if (health) out.health_concerns = health;
  // Per-person astro toggle (L1A). Default ON (absent === on). Only emit when the
  // user explicitly switched it OFF — so a non-L1A person's minimal shape is
  // unchanged (OQ-2 graceful skip), and an off survives the round-trip.
  if (p.astro_in_scope === false) out.astro_in_scope = false;
  return out;
}

/**
 * PURE — the human place-of-birth label for a person. Prefers the explicit
 * `place_of_birth` field; otherwise joins the structured city/state/country.
 * Returns '' when none is present.
 */
export function placeOfBirthLabel(p: PersonInput): string {
  const explicit = (p.place_of_birth || '').trim();
  if (explicit) return explicit;
  const parts = [p.pob_city, p.pob_state, p.pob_country]
    .map((x) => (x || '').trim())
    .filter(Boolean);
  return parts.join(', ');
}

/**
 * PURE — map a single form concern → the engine's
 * `answers.presenting_concerns[]` shape. The engine accepts a plain string OR
 * a `{text, concern_id?, severity?}` object; we always emit the object form
 * with `text` (so severity/id survive when present) and drop empty fields.
 */
export function concernToAnswer(c: ConcernInput): Record<string, unknown> {
  const out: Record<string, unknown> = { text: (c.text || '').trim() };
  const id = trimOrUndef(c.concern_id);
  if (id) out.concern_id = id;
  const sev = trimOrUndef(c.severity);
  if (sev) out.severity = sev;
  // L1A redesign — two aspects per area. Emit only when true (absent === unmarked,
  // which the report tolerates); the optional verbatim note is preserved as typed.
  if (c.issue === true) out.issue = true;
  if (c.aspiration === true) out.aspiration = true;
  const note = trimOrUndef(c.note);
  if (note) out.note = note;
  const applies = trimOrUndef(c.applies_to_person);
  if (applies) out.applies_to_person = applies;
  return out;
}

/**
 * PURE — map a single form property → the engine's `answers.properties[]` shape.
 * Drops blank floors + media rows; coerces areas/coords. A property is kept when
 * it has a label OR a use OR any location/area/floor/media (a fully-blank row is
 * dropped by the caller).
 */
export function propertyToAnswer(p: PropertyInput): Record<string, unknown> {
  const out: Record<string, unknown> = { id: p.id };
  const label = trimOrUndef(p.label);
  if (label) out.label = label;
  const use = trimOrUndef(p.use);
  if (use) out.use = use;
  const url = trimOrUndef(p.google_maps_url);
  if (url) out.google_maps_url = url;
  const coord = trimOrUndef(p.coordPair);
  if (coord) out.coordPair = coord;
  const north = numOrUndef(p.northRotation);
  if (north !== undefined) out.northRotation = north;
  const northShot = trimOrUndef(p.northScreenshotUrl);
  if (northShot) out.northScreenshotUrl = northShot;
  const built = numOrUndef(p.builtUpAreaSqft);
  if (built !== undefined) out.builtUpAreaSqft = built;
  const plot = numOrUndef(p.plotAreaSqft);
  if (plot !== undefined) out.plotAreaSqft = plot;
  const propertyId = trimOrUndef(p.propertyId);
  if (propertyId) out.propertyId = propertyId;
  // L1B drawing gate (2026-06-23) — 'yes' (plan uploaded) / 'no' (needs survey).
  if (p.hasDrawing === 'yes' || p.hasDrawing === 'no') out.hasDrawing = p.hasDrawing;
  // floors[] — keep a floor with a label OR an area OR a plan.
  const floors = (p.floors || [])
    .filter((f) => (f.floor || '').trim() || (f.areaApprox || '').trim() || f.planUrl)
    .map(floorToAnswer);
  if (floors.length) out.floors = floors;
  // media[] — keep an item with a url.
  const media = (p.media || [])
    .filter((m) => (m.url || '').trim())
    .map((m) => {
      const mo: Record<string, unknown> = {
        id: m.id,
        url: m.url,
        type: m.type === 'video' ? 'video' : 'image',
      };
      if (m.mimeType) mo.mimeType = m.mimeType;
      const floor = trimOrUndef(m.floor);
      if (floor) mo.floor = floor;
      const place = trimOrUndef(m.place);
      if (place) mo.place = place;
      if (m.storagePath) mo.storagePath = m.storagePath;
      return mo;
    });
  if (media.length) out.media = media;
  return out;
}

/**
 * PURE — minimal lat/lng extraction from a form property's location inputs
 * (the `coordPair` free-text or a Google Maps URL). Self-contained (no firebase
 * import) so it can be exercised in the pure test loader. Mirrors the subset of
 * geocode.ts/parseCoordsString needed for the proximity dedup seed.
 *
 * Handles: "lat, lng" decimal pair, "@lat,lng" and "!3d<lat>!4d<lng>" URL forms.
 * Returns null when nothing usable is present.
 */
export function parseLatLngFromProperty(
  p: Pick<PropertyInput, 'coordPair' | 'google_maps_url'>,
): { lat: number; lng: number } | null {
  const inRange = (lat: number, lng: number) =>
    Number.isFinite(lat) && Number.isFinite(lng) &&
    lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  const sources = [p.coordPair ?? '', p.google_maps_url ?? ''];
  for (const raw of sources) {
    const s = (raw || '').trim();
    if (!s) continue;
    // @lat,lng (Maps view centre)
    const at = s.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (at) {
      const lat = parseFloat(at[1]); const lng = parseFloat(at[2]);
      if (inRange(lat, lng)) return { lat, lng };
    }
    // !3d<lat>!4d<lng> (pin form)
    const pin = s.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
    if (pin) {
      const lat = parseFloat(pin[1]); const lng = parseFloat(pin[2]);
      if (inRange(lat, lng)) return { lat, lng };
    }
    // plain decimal pair "lat, lng" or "lat lng"
    const dec = s.match(/^(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)$/);
    if (dec) {
      const lat = parseFloat(dec[1]); const lng = parseFloat(dec[2]);
      if (inRange(lat, lng)) return { lat, lng };
    }
  }
  return null;
}

/**
 * PURE — map a form `PropertyInput` → a `PropertySeed` for `createProperty`
 * (AD-036 intake→property bridge). Carries address label, project type, north,
 * and a `siteLocation` (mapsUrl + decimal lat/lng) so the proximity dedup +
 * registry have the building's location. Returns the plain object shape
 * (`PropertySeed` from propertyModel) — kept here so it sits with PropertyInput.
 */
export function propertyInputToSeed(p: PropertyInput): {
  address?: string;
  projectType?: ProjectType;
  northRotation?: number;
  siteLocation?: {
    mapsUrl?: string;
    lat?: number;
    lng?: number;
    rawCoords?: string;
    coordFormat?: 'decimal';
  };
} {
  const seed: ReturnType<typeof propertyInputToSeed> = {};
  const label = trimOrUndef(p.label);
  if (label) seed.address = label;
  const use = trimOrUndef(p.use);
  if (use) seed.projectType = use as ProjectType;
  const north = numOrUndef(p.northRotation);
  if (north !== undefined) seed.northRotation = north;

  const sl: NonNullable<ReturnType<typeof propertyInputToSeed>['siteLocation']> = {};
  const url = trimOrUndef(p.google_maps_url);
  if (url) sl.mapsUrl = url;
  const coord = trimOrUndef(p.coordPair);
  if (coord) sl.rawCoords = coord;
  const ll = parseLatLngFromProperty(p);
  if (ll) {
    sl.lat = ll.lat;
    sl.lng = ll.lng;
    sl.coordFormat = 'decimal';
  }
  if (Object.keys(sl).length) seed.siteLocation = sl;
  return seed;
}

/**
 * AD-036.2 (2026-06-24) — PURE adapter: present the drawings-stage property block
 * (`DrawingsInput`) as a synthetic `PropertyInput` so the SAME unified
 * `PropertySlotLink` control + `propertyInputToSeed` work on the L1C/L2C drawings
 * stage without duplicating the registry logic. The drawings block has no label /
 * use of its own, so `address` + `use` are passed in from the surrounding form
 * (property_address / project_type) — the created property gets a meaningful
 * address. `floors` / `media` are not part of the property identity, so the
 * synthetic slot carries empty arrays (drawings live in `versions[]`, unchanged).
 */
export function drawingsToPropertyInput(
  d: DrawingsInput,
  opts?: { address?: string; use?: ProjectType | '' },
): PropertyInput {
  return {
    id: 'drawings_property',
    label: trimOrUndef(opts?.address) ?? '',
    use: opts?.use ?? '',
    google_maps_url: d.google_maps_url || '',
    coordPair: d.coordPair || '',
    northRotation: d.northRotation,
    northScreenshotUrl: d.northScreenshotUrl,
    builtUpAreaSqft: d.builtUpAreaSqft,
    plotAreaSqft: d.plotAreaSqft,
    floors: [],
    media: [],
    propertyId: d.propertyId,
  };
}

/**
 * AD-036.2 — PURE: map a `Partial<PropertyInput>` patch (as emitted by
 * `PropertySlotLink.onPatchSlot`, e.g. `{ propertyId }` on link/create or
 * `{ propertyId: undefined }` on unlink) back onto the `DrawingsInput` property
 * block. Only the registry-owned `propertyId` field is threaded back — the
 * capture fields on the drawings stage are edited directly via `patchD`, not
 * through this synthetic slot.
 */
export function applyPropertyPatchToDrawings(
  patch: Partial<PropertyInput>,
): Partial<DrawingsInput> {
  const out: Partial<DrawingsInput> = {};
  if ('propertyId' in patch) out.propertyId = patch.propertyId;
  return out;
}

/** PURE — one floor (label + area + plan) → its persisted shape. Shared by the
 * property serializer and the drawings-version serializer. */
export function floorToAnswer(f: FloorInput): Record<string, unknown> {
  const fo: Record<string, unknown> = { id: f.id, floor: (f.floor || '').trim() };
  const area = numOrUndef(f.areaApprox);
  if (area !== undefined) fo.areaApprox = area;
  if (f.planUrl) fo.planUrl = f.planUrl;
  if (f.planStoragePath) fo.planStoragePath = f.planStoragePath;
  if (f.planName) fo.planName = (f.planName || '').trim();
  if (f.planMime) fo.planMime = f.planMime;
  return fo;
}

/**
 * PURE — the L1C/L2C drawings block (v2) → answers shape. The PROPERTY (site
 * location, true-north, area) is at the top; `versions[]` holds each revised
 * drawing set (L2C) — for L1C there is exactly one. Architect contacts are
 * emitted only when present (the renderer hides them on L1C anyway). Empty
 * optional fields dropped; a version with no meaningful content is dropped.
 */
export function drawingsToAnswer(d: DrawingsInput): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const url = trimOrUndef(d.google_maps_url);
  if (url) out.google_maps_url = url;
  const coord = trimOrUndef(d.coordPair);
  if (coord) out.coordPair = coord;
  const north = numOrUndef(d.northRotation);
  if (north !== undefined) out.northRotation = north;
  const northShot = trimOrUndef(d.northScreenshotUrl);
  if (northShot) out.northScreenshotUrl = northShot;
  const built = numOrUndef(d.builtUpAreaSqft);
  if (built !== undefined) out.builtUpAreaSqft = built;
  const plot = numOrUndef(d.plotAreaSqft);
  if (plot !== undefined) out.plotAreaSqft = plot;
  // AD-036.2 — the linked Property entity id (P#####), when present.
  const pid = trimOrUndef(d.propertyId);
  if (pid) out.propertyId = pid;
  const versions = (d.versions || [])
    .map((v) => {
      const floors = (v.floors || [])
        .filter((f) => (f.floor || '').trim() || (f.areaApprox || '').trim() || f.planUrl)
        .map(floorToAnswer);
      const vo: Record<string, unknown> = { id: v.id };
      const stage = trimOrUndef(v.stage);
      if (stage) vo.stage = stage;
      const label = trimOrUndef(v.label);
      if (label) vo.label = label;
      if (floors.length) vo.floors = floors;
      return vo;
    })
    // Keep a version only if it carries a stage, a label, or any floor content.
    .filter((vo) => vo.stage || vo.label || (vo.floors as unknown[] | undefined)?.length);
  if (versions.length) out.versions = versions;
  // Folded-in "About the building" fields (full merge 2026-06-24).
  const mediaNote = trimOrUndef(d.mediaNote);
  if (mediaNote) out.mediaNote = mediaNote;
  const surveyStatus = trimOrUndef(d.surveyStatus);
  if (surveyStatus) out.surveyStatus = surveyStatus;
  const existingDrawings = trimOrUndef(d.existingDrawings);
  if (existingDrawings) out.existingDrawings = existingDrawings;
  const siteConstraints = trimOrUndef(d.siteConstraints);
  if (siteConstraints) out.siteConstraints = siteConstraints;
  // Architect + interior designer — structured Name·Email·WhatsApp (L2C only).
  const aName = trimOrUndef(d.architect_name);
  if (aName) out.architect_name = aName;
  const aEmail = trimOrUndef(d.architect_email);
  if (aEmail) out.architect_email = aEmail;
  const aWa = trimOrUndef(d.architect_whatsapp);
  if (aWa) out.architect_whatsapp = aWa;
  const aContact = trimOrUndef(d.architect_contact);
  if (aContact) out.architect_contact = aContact; // legacy back-compat
  const idName = trimOrUndef(d.interior_designer_name);
  if (idName) out.interior_designer_name = idName;
  const idEmail = trimOrUndef(d.interior_designer_email);
  if (idEmail) out.interior_designer_email = idEmail;
  const idWa = trimOrUndef(d.interior_designer_whatsapp);
  if (idWa) out.interior_designer_whatsapp = idWa;
  const idesigner = trimOrUndef(d.interior_designer);
  if (idesigner) out.interior_designer = idesigner; // legacy back-compat
  const visit = trimOrUndef(d.site_visit_readiness);
  if (visit) out.site_visit_readiness = visit;
  return out;
}

/** PURE — a persisted floor → FloorInput. Shared by the property + drawings loaders. */
export function floorFromAnswer(raw: unknown): FloorInput {
  const fo = (raw || {}) as Record<string, unknown>;
  return {
    id: asStr(fo.id) || newRowId('fl'),
    floor: asStr(fo.floor),
    areaApprox: asNumStr(fo.areaApprox),
    planUrl: asStr(fo.planUrl) || undefined,
    planStoragePath: asStr(fo.planStoragePath) || undefined,
    planName: asStr(fo.planName) || undefined,
    planMime: asStr(fo.planMime) || undefined,
  };
}

/** PURE — a persisted `answers.drawings` map → DrawingsInput (defensive coercion). */
export function drawingsFromAnswer(raw: unknown): DrawingsInput {
  const d = (raw || {}) as Record<string, unknown>;
  const versionsRaw = Array.isArray(d.versions) ? d.versions : [];
  const stageOf = (v: unknown): DrawingStage | '' => {
    const s = asStr(v);
    return (['concept', 'schematic', 'developed', 'working', 'other'] as const).includes(
      s as DrawingStage,
    )
      ? (s as DrawingStage)
      : '';
  };
  return {
    google_maps_url: asStr(d.google_maps_url),
    coordPair: asStr(d.coordPair),
    northRotation: asNumStr(d.northRotation),
    northScreenshotUrl: asStr(d.northScreenshotUrl) || undefined,
    builtUpAreaSqft: asNumStr(d.builtUpAreaSqft),
    plotAreaSqft: asNumStr(d.plotAreaSqft),
    propertyId: asStr(d.propertyId) || undefined,
    versions: versionsRaw.map((v) => {
      const vo = (v || {}) as Record<string, unknown>;
      const floorsRaw = Array.isArray(vo.floors) ? vo.floors : [];
      return {
        id: asStr(vo.id) || newRowId('dv'),
        stage: stageOf(vo.stage),
        label: asStr(vo.label),
        floors: floorsRaw.map(floorFromAnswer),
      } as DrawingVersion;
    }),
    mediaNote: asStr(d.mediaNote) || undefined,
    surveyStatus: asStr(d.surveyStatus) || undefined,
    existingDrawings: asStr(d.existingDrawings) || undefined,
    siteConstraints: asStr(d.siteConstraints) || undefined,
    architect_name: asStr(d.architect_name) || undefined,
    architect_email: asStr(d.architect_email) || undefined,
    architect_whatsapp: asStr(d.architect_whatsapp) || undefined,
    architect_contact: asStr(d.architect_contact) || undefined,
    interior_designer_name: asStr(d.interior_designer_name) || undefined,
    interior_designer_email: asStr(d.interior_designer_email) || undefined,
    interior_designer_whatsapp: asStr(d.interior_designer_whatsapp) || undefined,
    interior_designer: asStr(d.interior_designer) || undefined,
    site_visit_readiness: asStr(d.site_visit_readiness) || undefined,
  };
}

/**
 * PURE — the heart of Slice 1. Turn the form state into the engine's
 * `answers.*` object, EXACTLY as `clients/{id}/intake/L2B_R1.answers`. Empty
 * optional fields are dropped (the engine treats absent == not-provided);
 * required-but-empty fields are written as '' so the doc shape is stable and
 * the renderer fails loudly rather than silently.
 *
 * It writes ONLY the client-owned Slice-1 fields. It NEVER emits
 * north_rotation, floors[], boundary, fixtures, or entrance codes — those are
 * staff/technical (Slices 2–4) and a `merge:true` save must not clobber them.
 */
export function buildIntakeAnswers(form: IntakeForm): Record<string, unknown> {
  const answers: Record<string, unknown> = {
    client_name: (form.client_name || '').trim(),
    property_address: (form.property_address || '').trim(),
    project_type: form.project_type || '',
    country: (form.country || '').trim(),
    language: form.language || 'en',
    astro_in_scope: form.astro_in_scope === true,
  };

  const firm = trimOrUndef(form.firm);
  if (firm) answers.firm = firm;
  const scope = trimOrUndef(form.scope_label);
  if (scope) answers.scope_label = scope;

  // L1A redesign — additive client-owned fields. email/whatsapp are required at
  // L1A but written as '' (stable shape) so the renderer can fail loudly; address
  // is optional (dropped when blank). intended_use is the L1A mandate.
  answers.email = (form.email || '').trim();
  answers.whatsapp = (form.whatsapp || '').trim();
  const addr = trimOrUndef(form.address);
  if (addr) answers.address = addr;
  answers.intended_use = form.intended_use || '';

  // properties[] — candidate properties (L1A multi-property). Drop a row with no
  // MEANINGFUL content. NOTE: `use` alone does NOT keep a row — a fresh candidate
  // inherits the form's intended_use, so a row with only an inherited use (and
  // nothing the client actually typed) is still "blank".
  const properties = (form.properties || [])
    .filter(
      (p) =>
        (p.label || '').trim() ||
        (p.google_maps_url || '').trim() ||
        (p.coordPair || '').trim() ||
        (p.builtUpAreaSqft || '').trim() ||
        (p.plotAreaSqft || '').trim() ||
        p.hasDrawing === 'yes' || p.hasDrawing === 'no' ||
        (p.floors || []).some((f) => (f.floor || '').trim() || (f.areaApprox || '').trim() || f.planUrl) ||
        (p.media || []).some((m) => (m.url || '').trim()),
    )
    .map(propertyToAnswer);
  if (properties.length) answers.properties = properties;

  // presenting_concerns[] — drop blank rows.
  const concerns = (form.presenting_concerns || [])
    .filter((c) => (c.text || '').trim())
    .map(concernToAnswer);
  answers.presenting_concerns = concerns;

  // goals[] — drop blank rows, trim.
  const goals = (form.goals || [])
    .map((g) => (g || '').trim())
    .filter(Boolean);
  answers.goals = goals;

  // persons[] — drop fully-blank rows (no name AND no id-meaning); keep an
  // incomplete-but-named person (engine skips charting, no error).
  const persons = (form.persons || [])
    .filter((p) => (p.name || '').trim())
    .map(personToAnswer);
  answers.persons = persons;

  // follow_up.start_date — only when provided.
  const startDate = trimOrUndef(form.follow_up_start_date);
  if (startDate) answers.follow_up = { start_date: startDate };

  // L1B Plans & preferences (Box 7) — "what have you tried" (verbatim) + the four
  // willingness single-picks. Each emitted only when filled (absent === unset).
  const tried = trimOrUndef(form.prior_remedies_tried);
  if (tried) answers.prior_remedies_tried = tried;
  const w = form.willingness || {};
  const willOut: Record<string, string> = {};
  for (const k of ['implement', 'structural', 'discuss', 'follow_process'] as const) {
    const v = trimOrUndef(w[k]);
    if (v) willOut[k] = v;
  }
  if (Object.keys(willOut).length) answers.willingness = willOut;

  // L1C/L2C drawings (v2) — emit only when the block carries any content (property
  // location/north/area or a drawing version). Absent on tiers that don't capture
  // architect drawings, so a merge:true save never adds an empty `drawings` map.
  const drawingsOut = drawingsToAnswer(form.drawings || blankDrawings());
  if (Object.keys(drawingsOut).length) answers.drawings = drawingsOut;

  // AD-040 — structured Design Programme. Emit only when it carries content, so
  // a merge:true save on a tier without the module never writes an empty map.
  const dp = form.designProgramme || blankDesignProgramme();
  if (programmeHasContent(dp)) {
    const dpOut = programmeToAnswer(dp);
    if (Object.keys(dpOut).length) answers.designProgramme = dpOut;
  }

  // AD-042 — L2D structured Construction details. Emit only when it carries content.
  const cd = form.constructionDetails || blankConstructionDetails();
  if (constructionHasContent(cd)) {
    const cdOut = constructionToAnswer(cd);
    if (Object.keys(cdOut).length) answers.constructionDetails = cdOut;
  }

  // AD-042 — L3B Plans & preferences. Emit only when it carries content.
  const pp = form.designPlansPrefs || blankDesignPlansPrefs();
  if (plansPrefsHasContent(pp)) {
    const ppOut = plansPrefsToAnswer(pp);
    if (Object.keys(ppOut).length) answers.designPlansPrefs = ppOut;
  }

  return answers;
}

/**
 * PURE — the full doc envelope (minus the server `updated_at`, which is filled
 * impurely at the write site). This is what `saveIntake` merges into the doc.
 */
export function buildIntakeEnvelope(
  form: IntakeForm,
): Record<string, unknown> {
  return {
    schema_version: INTAKE_SCHEMA_VERSION,
    tier: L2B_R1_TIER,
    answers: buildIntakeAnswers(form),
  };
}

/* ───────────────────────── resume / edit mapping ──────────────────────── */

function asStr(v: unknown): string {
  return typeof v === 'string' ? v : v == null ? '' : String(v);
}
function asNumStr(v: unknown): string {
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  if (typeof v === 'string' && v.trim()) return v.trim();
  return '';
}

/**
 * PURE — the inverse: hydrate the form from an existing intake doc's `answers`
 * so a returning client/staff edits rather than re-enters. Defensive across a
 * partial or pre-Slice-1 doc; reads ONLY the client-owned fields it owns and
 * leaves any staff/technical fields untouched (they are not in the form).
 */
export function formFromAnswers(
  answers: Record<string, unknown> | null | undefined,
): IntakeForm {
  const a = answers || {};
  const base = blankIntakeForm();

  const pt = asStr(a.project_type) as ProjectType | '';
  const lang = asStr(a.language) as Language;

  const concernsRaw = Array.isArray(a.presenting_concerns)
    ? a.presenting_concerns
    : [];
  const concerns: ConcernInput[] = concernsRaw.map((c) => {
    if (typeof c === 'string') return { text: c, severity: '' };
    const o = (c || {}) as Record<string, unknown>;
    return {
      text: asStr(o.text),
      severity: (asStr(o.severity) as Severity) || '',
      concern_id: asStr(o.concern_id) || undefined,
      // L1A redesign — two aspects + verbatim note (tolerate absent).
      issue: o.issue === true,
      aspiration: o.aspiration === true,
      note: asStr(o.note) || undefined,
      applies_to_person: asStr(o.applies_to_person) || undefined,
    };
  });

  const goalsRaw = Array.isArray(a.goals) ? a.goals : [];
  const goals: string[] = goalsRaw.map((g) => asStr(g)).filter(Boolean);

  const personsRaw = Array.isArray(a.persons) ? a.persons : [];
  const persons: PersonInput[] = personsRaw.map((p) => {
    const o = (p || {}) as Record<string, unknown>;
    return {
      id: asStr(o.id) || newRowId('p'),
      name: asStr(o.name),
      role: asStr(o.role || o.relationship),
      // AD-027 Axis 2 — pre-fill the captured relation; tolerate the legacy
      // `relationship` field carrying a familial value on a pre-S5 doc.
      relation_to_client: asStr(o.relation_to_client) || undefined,
      // AD-027 Axis 3 — pre-fill the captured work/reporting axis.
      work_role: asStr(o.work_role) || undefined,
      reports_to: asStr(o.reports_to) || undefined,
      department: asStr(o.department) || undefined,
      dob: asStr(o.dob),
      time_of_birth: asStr(o.time_of_birth || o.tob || o.birth_time),
      place_of_birth: asStr(o.place_of_birth),
      // L1A redesign — structured place of birth + per-person flags.
      pob_city: asStr(o.pob_city) || undefined,
      pob_state: asStr(o.pob_state) || undefined,
      pob_country: asStr(o.pob_country) || undefined,
      lat: asNumStr(o.lat),
      lon: asNumStr(o.lon),
      tz: asStr(o.tz),
      is_breadwinner: o.is_breadwinner === true,
      // STANDARD per-person Health module (2026-06-23) — round-trip the optional
      // free-text concern so a returning client/staff sees it pre-filled.
      health_concerns: asStr(o.health_concerns) || undefined,
      // Per-person astro: default true (L1A on-by-default); a saved explicit
      // false stays off.
      astro_in_scope: o.astro_in_scope !== false,
    };
  });

  const propertiesRaw = Array.isArray(a.properties) ? a.properties : [];
  const properties: PropertyInput[] = propertiesRaw.map((p) => {
    const o = (p || {}) as Record<string, unknown>;
    const useRaw = asStr(o.use) as ProjectType | '';
    const floorsRaw = Array.isArray(o.floors) ? o.floors : [];
    const mediaRaw = Array.isArray(o.media) ? o.media : [];
    return {
      id: asStr(o.id) || newRowId('prop'),
      label: asStr(o.label),
      use: PROJECT_TYPES.includes(useRaw as ProjectType) ? useRaw : '',
      google_maps_url: asStr(o.google_maps_url),
      coordPair: asStr(o.coordPair),
      northRotation: asNumStr(o.northRotation),
      northScreenshotUrl: asStr(o.northScreenshotUrl) || undefined,
      builtUpAreaSqft: asNumStr(o.builtUpAreaSqft),
      plotAreaSqft: asNumStr(o.plotAreaSqft),
      propertyId: asStr(o.propertyId) || undefined,
      hasDrawing: asStr(o.hasDrawing) === 'yes' ? 'yes' : asStr(o.hasDrawing) === 'no' ? 'no' : undefined,
      floors: floorsRaw.map(floorFromAnswer),
      media: mediaRaw.map((m) => {
        const mo = (m || {}) as Record<string, unknown>;
        return {
          id: asStr(mo.id) || newRowId('media'),
          url: asStr(mo.url),
          type: asStr(mo.type) === 'video' ? 'video' : 'image',
          mimeType: asStr(mo.mimeType) || undefined,
          floor: asStr(mo.floor),
          place: asStr(mo.place),
          storagePath: asStr(mo.storagePath) || undefined,
        } as MediaInput;
      }),
    };
  });

  const followUp = (a.follow_up || {}) as Record<string, unknown>;
  const useRaw = asStr(a.intended_use) as ProjectType | '';

  // L1B Plans & preferences (Box 7) — round-trip willingness + "what have you tried".
  const willRaw = (a.willingness || {}) as Record<string, unknown>;
  const willedge: WillingnessAnswers = {};
  for (const k of ['implement', 'structural', 'discuss', 'follow_process'] as const) {
    const v = asStr(willRaw[k]);
    if (v) willedge[k] = v;
  }

  return {
    ...base,
    client_name: asStr(a.client_name),
    firm: asStr(a.firm),
    property_address: asStr(a.property_address),
    project_type: PROJECT_TYPES.includes(pt as ProjectType) ? pt : '',
    country: asStr(a.country) || base.country,
    scope_label: asStr(a.scope_label),
    language: LANGUAGES.includes(lang) ? lang : 'en',
    presenting_concerns: concerns,
    goals,
    astro_in_scope: a.astro_in_scope === true,
    persons,
    follow_up_start_date: asStr(followUp.start_date),
    // L1A redesign additive fields.
    email: asStr(a.email),
    whatsapp: asStr(a.whatsapp),
    address: asStr(a.address),
    intended_use: PROJECT_TYPES.includes(useRaw as ProjectType) ? useRaw : '',
    properties,
    // L1C/L2C drawings (v2) — round-trip the property block + versions.
    drawings: drawingsFromAnswer(a.drawings),
    designProgramme: programmeFromAnswer(a.designProgramme),
    constructionDetails: constructionFromAnswer(a.constructionDetails),
    designPlansPrefs: plansPrefsFromAnswer(a.designPlansPrefs),
    // L1B Plans & preferences (Box 7).
    prior_remedies_tried: asStr(a.prior_remedies_tried),
    willingness: willedge,
  };
}

/* ──────────────────────────── validation ─────────────────────────────── */

/**
 * PURE — block save only on the truly-required client-owned fields. Staff fill
 * the technical requireds later; this slice gates on what the client owns.
 * Returns an array of human messages ([] == ok to save).
 */
export function validateIntakeForm(form: IntakeForm): string[] {
  const errs: string[] = [];
  if (!(form.client_name || '').trim()) errs.push('Client name is required.');
  if (!(form.property_address || '').trim())
    errs.push('Property address is required.');
  if (!form.project_type) errs.push('Project type is required.');
  if (!(form.country || '').trim()) errs.push('Country is required.');
  return errs;
}

/* ───────────────────── L1A redesign — module validators ───────────────────── */

/**
 * PURE — L1A "About you" required fields: name, email, WhatsApp, country.
 * (No property_address / project_type — those moved out of identity at L1A.)
 */
export function validateL1AIdentity(form: IntakeForm): string[] {
  const errs: string[] = [];
  if (!(form.client_name || '').trim()) errs.push('Your name is required.');
  if (!(form.email || '').trim()) errs.push('Email is required.');
  if (!(form.whatsapp || '').trim()) errs.push('WhatsApp number is required.');
  if (!(form.country || '').trim()) errs.push('Country is required.');
  return errs;
}

/** PURE — L1A "Goal & intended use": intended_use is the mandate (required). */
export function validateL1AGoal(form: IntakeForm): string[] {
  const errs: string[] = [];
  if (!form.intended_use) errs.push('Please choose what you are selecting a property for.');
  return errs;
}

/**
 * PURE — L1A property details. Site location: for EACH property that has any
 * detail entered, at least ONE of {Google Maps link, coordinates} must be
 * filled (neither is individually mandatory, but one is required to locate the
 * site). A wholly-blank extra property row is ignored (the default first row is
 * not blocked until the client starts filling it).
 */
export function validateL1AProperty(form: IntakeForm): string[] {
  const errs: string[] = [];
  const props = form.properties || [];
  props.forEach((p, i) => {
    const touched = !!(
      (p.label || '').trim() ||
      p.use ||
      (p.builtUpAreaSqft || '').trim() ||
      (p.plotAreaSqft || '').trim() ||
      (p.floors || []).some((f) => (f.floor || '').trim() || (f.areaApprox || '').trim()) ||
      (p.media || []).length
    );
    const hasLink = !!(p.google_maps_url || '').trim();
    const hasCoords = !!(p.coordPair || '').trim();
    // Block when: this property is being filled (or is the only one), but has
    // neither a maps link nor coordinates.
    const onlyProperty = props.length === 1;
    if ((touched || onlyProperty || hasLink || hasCoords) && !hasLink && !hasCoords) {
      const name = (p.label || '').trim() || `Property ${i + 1}`;
      errs.push(`${name}: add a Google Maps link or coordinates (at least one is required).`);
    }
  });
  return errs;
}

/**
 * PURE — L1A People conditional-required validation. For each NAMED person whose
 * per-person astrology is ON, birth fields (DOB, time, place-of-birth city +
 * country, coordinates) are required. A person with astrology OFF is never
 * blocked. Returns human messages ([] == ok).
 */
export function validateL1APeople(form: IntakeForm): string[] {
  const errs: string[] = [];
  for (const p of form.persons || []) {
    const name = (p.name || '').trim();
    if (!name) continue;
    if (p.astro_in_scope === false) continue; // astro off → birth optional
    const missing: string[] = [];
    if (!trimOrUndef(p.dob)) missing.push('date of birth');
    if (!trimOrUndef(p.time_of_birth)) missing.push('time of birth');
    if (!trimOrUndef(p.pob_city)) missing.push('birth city');
    if (!trimOrUndef(p.pob_country)) missing.push('birth country');
    if (numOrUndef(p.lat) === undefined || numOrUndef(p.lon) === undefined)
      missing.push('birth coordinates');
    if (missing.length) {
      errs.push(`${name}: ${missing.join(', ')} required (or switch off astrology for them).`);
    }
  }
  return errs;
}

/* ───────────────────── L1B (2026-06-23) — module validators ───────────────────── */

/**
 * PURE — L1B "About you & contact" required fields: the L1A identity set PLUS
 * the property address (L1B always reviews one space the client occupies).
 */
export function validateL1BIdentity(form: IntakeForm): string[] {
  const errs = validateL1AIdentity(form);
  if (!(form.property_address || '').trim())
    errs.push('The address of the property being reviewed is required.');
  return errs;
}

/**
 * PURE — L1B property details (single occupied space). Reuses the L1A site-
 * location requirement (link or coordinates) on the first property, PLUS the
 * drawing gate: the client must answer "do you have a floor plan?", and when
 * they say YES a plan must be attached on at least one floor (the plan is what
 * lets us grid). Saying NO is allowed (it routes to the survey/sketch path) and
 * never blocks the form.
 */
export function validateL1BProperty(form: IntakeForm): string[] {
  const errs = validateL1AProperty(form);
  const p = (form.properties || [])[0];
  if (!p || (p.hasDrawing !== 'yes' && p.hasDrawing !== 'no')) {
    errs.push('Please tell us whether you have a floor plan / drawing of this space.');
    return errs;
  }
  if (p.hasDrawing === 'yes') {
    const hasPlan = (p.floors || []).some((f) => !!f.planUrl || !!f.planStoragePath);
    if (!hasPlan)
      errs.push('You said you have a drawing — please upload at least one floor plan (or switch to "No").');
  }
  return errs;
}

/**
 * PURE — L1B plans & preferences. Three willingness questions are required
 * (implement · discuss · follow a proven process); structural change is optional
 * ("rarest case, if at all"). "What have you tried" is optional.
 */
export function validateL1BPlans(form: IntakeForm): string[] {
  const errs: string[] = [];
  const w = form.willingness || {};
  if (!trimOrUndef(w.implement))
    errs.push('Please tell us your willingness to implement the remedies.');
  if (!trimOrUndef(w.discuss))
    errs.push('Please tell us your willingness to discuss & give us your time.');
  if (!trimOrUndef(w.follow_process))
    errs.push('Please tell us your willingness to follow a proven process.');
  return errs;
}

/* ───────────────── L1C / L2C drawings_upload validator (v2) ───────────────── */

/**
 * PURE — the drawings-upload module (L1C/L2C). Required:
 *  - site location: a Google Maps link OR coordinates (at least one);
 *  - at least one drawing version that carries at least one uploaded plan file.
 * Architect contact is NOT validated here (L2C shows it but the joint-call detail
 * can follow; the bright line hides it entirely on L1C).
 */
export function validateDrawingsUpload(form: IntakeForm): string[] {
  const errs: string[] = [];
  const d = form.drawings || blankDrawings();
  const hasLink = !!trimOrUndef(d.google_maps_url);
  const hasCoords = !!trimOrUndef(d.coordPair);
  if (!hasLink && !hasCoords)
    errs.push('Add the site location — a Google Maps link or coordinates (at least one is required).');
  const anyPlan = (d.versions || []).some((v) =>
    (v.floors || []).some((f) => !!f.planUrl || !!f.planStoragePath),
  );
  if (!anyPlan)
    errs.push('Upload at least one floor drawing (DWG / DXF / PDF / image) on a drawing version.');
  return errs;
}

/**
 * PURE — when astro IS in scope, surface (non-blocking) which named persons
 * have INCOMPLETE birth data, so the form can warn that they will be skipped.
 * Returns the names; [] when astro is off or all complete. Never an error —
 * OQ-2 says an incomplete person is simply saved without a chart.
 */
export function personsMissingBirthData(form: IntakeForm): string[] {
  if (!form.astro_in_scope) return [];
  return (form.persons || [])
    .filter((p) => (p.name || '').trim())
    .filter((p) => {
      const hasDob = !!trimOrUndef(p.dob);
      const hasTob = !!trimOrUndef(p.time_of_birth);
      const hasLat = numOrUndef(p.lat) !== undefined;
      const hasLon = numOrUndef(p.lon) !== undefined;
      return !(hasDob && hasTob && hasLat && hasLon);
    })
    .map((p) => p.name.trim());
}

/* ─────────────────────────── impure (Firestore) ───────────────────────── */
/* Everything below imports `db`; the Node test runner never loads it (the
 * tests import only the pure exports above via _tsload, which short-circuits
 * before touching these). Keep all Firebase access at the bottom. */

import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  collection,
  writeBatch,
  arrayUnion,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type { PersonDataSource } from '../types/personModel';
import { type IntakeScope, canonicalIntakePath } from './intakeScope';
import { loadIntakeDoc, writeIntakeDoc } from './intakeScopeDb';
import { nextClientSeq, clientNoFromSeq } from './projectStore';
import { geocode, countryCodeHint } from './geocoder';
import type { FamilyMember } from './familyStore';
import { pobDisplayName, type PlaceOfBirth } from './contactModel';

/**
 * Coerce a caller's scope argument into an `IntakeScope`. Accepts EITHER a bare
 * clientId string (legacy call shape — client-only scope) OR a full
 * `{ clientId, projectId? }` scope. This keeps every existing call site working
 * (passing just a clientId behaves exactly as before, client-scoped) while new
 * project-aware call sites pass the full scope. (AD-030)
 */
export function asScope(scopeOrClientId: IntakeScope | string): IntakeScope {
  return typeof scopeOrClientId === 'string'
    ? { clientId: scopeOrClientId }
    : scopeOrClientId;
}

/** The canonical doc path this slice reads/writes (project-scoped when known). */
export function intakeDocPath(
  scopeOrClientId: IntakeScope | string,
  tier: string = L2B_R1_TIER,
) {
  return canonicalIntakePath(asScope(scopeOrClientId), tier);
}

/**
 * The universal pre-quote SCOPING tier doc id (AD-031). The scoping form writes
 * the SAME shared `answers` doc as every other tier, just under this tier id, so
 * a later delivery-tier intake can carry its answers over (see `loadIntake`).
 */
export const SCOPING_TIER = 'SCOPING_R1' as const;

/**
 * Load an existing intake doc and hydrate the form (resume/edit). Returns the
 * blank form when no doc exists yet. Reads `answers` only — the staff/
 * technical fields stay on the doc, untouched.
 *
 * AD-030: project-scoped read with a backward-compatible fallback to the legacy
 * `clients/{cid}/intake/{tier}` doc when the project-scoped doc is absent.
 *
 * AD-031 (cross-phase auto-fill): when the requested tier has NO doc yet AND it
 * is a DELIVERY tier (not SCOPING itself, not a birth-only sidecar), fall back to
 * the project's SCOPING answers so the contact + goal + property answers captured
 * at scoping pre-fill the delivery intake. `existed` stays false (this is a fresh
 * delivery doc seeded from scoping — the first save persists it under the tier).
 */
export async function loadIntake(
  scopeOrClientId: IntakeScope | string,
  tier: string = L2B_R1_TIER,
): Promise<{ form: IntakeForm; existed: boolean; carriedFromScoping?: boolean }> {
  const scope = asScope(scopeOrClientId);
  const hit = await loadIntakeDoc(scope, tier);
  if (hit) {
    return {
      form: formFromAnswers(hit.data.answers as Record<string, unknown>),
      existed: true,
    };
  }
  // No doc for this tier — try carrying scoping answers over (AD-031).
  if (carriesFromScoping(tier)) {
    const scoped = await loadIntakeDoc(scope, SCOPING_TIER);
    if (scoped) {
      return {
        form: formFromAnswers(scoped.data.answers as Record<string, unknown>),
        existed: false,
        carriedFromScoping: true,
      };
    }
  }
  return { form: blankIntakeForm(), existed: false };
}

/**
 * PURE — should a tier carry the SCOPING answers forward when it has no doc yet?
 * Yes for every delivery tier; NO for SCOPING itself (no self-carry) and the
 * birth-only sidecar (a targeted top-up, not a fresh delivery form).
 */
export function carriesFromScoping(tier: string): boolean {
  const t = (tier || '').toUpperCase();
  if (t.startsWith('SCOPING')) return false;
  if (t.startsWith('BIRTH_ONLY')) return false;
  return true;
}

/**
 * MODULE 4 (Phase 3) — REPORT_ENGINE_SEAM.
 *
 * Merge-write the Property entity and Grid FK back into the intake answers doc.
 * This is the single write that establishes the seam — after it, `ensureGrids`
 * can read the grid from the `properties/{pid}/grids/{gridId}` subcollection
 * instead of recomputing from the boundary.
 *
 * Safe to call repeatedly — uses `{ merge: true }` so it never clobbers other
 * intake fields (boundary, doors, north, etc.).
 *
 * @param clientId  — the client doc id (e.g. 'C00001')
 * @param tier      — intake tier doc id (e.g. 'L2B_R1')
 * @param propertyId — the Property entity id (e.g. 'P00001')
 * @param gridId    — the active grid id, or null when grid not yet computed
 * @param actor     — for audit (not currently written but kept for consistency)
 */
export async function savePropertyLink(
  scopeOrClientId: IntakeScope | string,
  tier: string,
  propertyId: string,
  gridId: string | null,
  _actor: string,
): Promise<void> {
  const patch: Record<string, unknown> = {
    'answers.propertyId': propertyId,
    updated_at: serverTimestamp(),
  };
  if (gridId != null) patch['answers.gridId'] = gridId;
  await writeIntakeDoc(asScope(scopeOrClientId), tier, patch);
}

/**
 * MODULE 4 (Phase 3) — REPORT_ENGINE_SEAM (pure).
 *
 * Given the raw `answers` map from an intake doc, determine the data source
 * for floor/grid data:
 *
 *   - `{ type: 'entity', propertyId }` — a Property entity is linked; the
 *     report engine should read floor plans and grids from `properties/{pid}`.
 *   - `{ type: 'legacy', address, northRotation, floors }` — no Property link;
 *     the engine reads from the intake doc's `floors[]` array (the old path).
 *
 * Pure — no Firebase I/O, no side effects.
 */
export function resolvePropertySource(answers: Record<string, unknown>):
  | { type: 'entity'; propertyId: string }
  | { type: 'legacy'; address: string; northRotation: number | null; floors: unknown[] } {
  const pid = answers?.propertyId;
  if (typeof pid === 'string' && pid.trim()) {
    return { type: 'entity', propertyId: pid.trim() };
  }
  return {
    type: 'legacy',
    address: typeof answers?.property_address === 'string' ? answers.property_address : '',
    northRotation:
      typeof answers?.north_rotation === 'number' &&
      Number.isFinite(answers.north_rotation as number)
        ? (answers.north_rotation as number)
        : null,
    floors: Array.isArray(answers?.floors) ? (answers.floors as unknown[]) : [],
  };
}

/* ───────────────────── persons subcollection (AD-019 / AD-020) ────────────── */

/**
 * PURE — resolve the stable `personId` for a person ROW in the intake form.
 *
 * Convention (AD-019):
 *   • The FIRST named person row is the PRIMARY client  → `${clientId}_primary`.
 *   • Every other person row uses its own stable row id (`PersonInput.id`),
 *     which is persisted in the intake doc at `answers.persons[].id` and is
 *     preserved across re-submits by `formFromAnswers`. This gives each family
 *     member a stable personId without adding a new id mechanism.
 */
export function personIdForRow(
  clientId: string,
  rowIndex: number,
  rowId: string,
): string {
  if (rowIndex === 0) return `${clientId}_primary`;
  return rowId;
}

/**
 * PURE — map a form `PersonInput` → the AD-019 Person doc fields (minus the
 * server `data_source_updated_at`, which is filled impurely at the write site).
 *
 * Mirrors `personToAnswer`'s coercion (finite lat/lon only, trimmed strings,
 * absent rather than empty) but emits the AD-019 field names: `tob` (not
 * `time_of_birth`), `pob` (not `place_of_birth`), `relationship` (not `role`).
 * Optional fields are omitted when absent so a `merge:true` write never clobbers
 * a value already present (e.g. an email/whatsapp added on the contact card).
 */
export function personDocFromInput(
  p: PersonInput,
  dataSource: PersonDataSource,
): Record<string, unknown> {
  const out: Record<string, unknown> = {
    name: (p.name || '').trim(),
    data_source: dataSource,
  };
  // AD-027 — two-axis taxonomy. The form's `role` field carries Axis-1
  // participant_type (the S5 select). Emit it as the explicit `participant_type`
  // AND mirror it to the legacy `relationship` field for back-compat (so old
  // readers + the astro mirror keep seeing a value). Axis-2 relation_to_client is
  // emitted explicitly; both axes are merge-safe (absent → omitted).
  const ptype = trimOrUndef(p.role);
  if (ptype) {
    out.participant_type = ptype;
    out.relationship = ptype; // deprecated back-compat mirror
  }
  const relToClient = trimOrUndef(p.relation_to_client);
  if (relToClient) out.relation_to_client = relToClient;
  // AD-027 Axis 3 — work/reporting axis onto the Person doc (merge-safe; absent
  // → omitted). reports_to is the stable INTAKE ROW id (PersonInput.id) of another
  // person in this same intake — NOT the resolved person-doc id (row 0 maps to
  // `${cid}_primary` via personIdForRow). Keeping the raw row id is deliberate:
  // the UI offers/guards rows by their PersonInput.id, and the DEFERRED
  // org-hierarchy compute resolves row-id → personId when it builds the org graph.
  const workRole = trimOrUndef(p.work_role);
  if (workRole) out.work_role = workRole;
  const reportsTo = trimOrUndef(p.reports_to);
  if (reportsTo) out.reports_to = reportsTo;
  const department = trimOrUndef(p.department);
  if (department) out.department = department;
  const dob = trimOrUndef(p.dob);
  if (dob) out.dob = dob;
  const tob = trimOrUndef(p.time_of_birth);
  if (tob) out.tob = tob;
  // L1A redesign — pob label (explicit or derived from city/state/country) plus
  // the structured city/state/country, so the astro engine + report can both
  // read a single human label AND the precise components. Merge-safe (omitted
  // when absent so a value set elsewhere is never clobbered).
  const pob = placeOfBirthLabel(p);
  if (pob) out.pob = pob;
  const pobCity = trimOrUndef(p.pob_city);
  if (pobCity) out.pob_city = pobCity;
  const pobState = trimOrUndef(p.pob_state);
  if (pobState) out.pob_state = pobState;
  const pobCountry = trimOrUndef(p.pob_country);
  if (pobCountry) out.pob_country = pobCountry;
  const lat = numOrUndef(p.lat);
  if (lat !== undefined) out.lat = lat;
  const lon = numOrUndef(p.lon);
  if (lon !== undefined) out.lon = lon;
  const tz = trimOrUndef(p.tz);
  if (tz) out.tz = tz;
  if (p.is_breadwinner === true) out.is_breadwinner = true;
  // STANDARD per-person Health module (2026-06-23) — health is a per-person
  // attribute on the Person Data Hub (AD-008/010), read alongside astrology.
  // Merge-safe (omitted when absent so a value set elsewhere is never clobbered).
  const health = trimOrUndef(p.health_concerns);
  if (health) out.health_concerns = health;
  return out;
}

/**
 * Mirror the form's persons[] into `clients/{cid}/persons/{personId}` (AD-019).
 *
 * Each named person row becomes its own person doc (the primary client +
 * each family member). `data_source` is derived from WHO is filling the form
 * (`avbp_intake_client` on the public link path, `avbp_intake_staff` in the
 * portal). `data_source_updated_at` is a server timestamp. Writes use
 * `{ merge: true }` so they are idempotent and never clobber channel fields
 * (email/whatsapp) or a prakriti added elsewhere.
 *
 * A Cloud Function (`syncPersonToAstroEngine`, AD-020) mirrors each write to
 * `astro/_av/people/{personId}` — the portal does NOT write the astro doc.
 */
export async function savePersons(
  clientId: string,
  form: IntakeForm,
  via: 'client' | 'staff',
): Promise<void> {
  const dataSource: PersonDataSource =
    via === 'client' ? 'avbp_intake_client' : 'avbp_intake_staff';
  const named = (form.persons || []).filter((p) => (p.name || '').trim());
  await Promise.all(
    named.map((p, i) => {
      const personId = personIdForRow(clientId, i, p.id);
      const ref = doc(db, 'clients', clientId, 'persons', personId);
      return setDoc(
        ref,
        {
          ...personDocFromInput(p, dataSource),
          data_source_updated_at: serverTimestamp(),
        },
        { merge: true },
      );
    }),
  );
}

/**
 * M5-S5 — write ONE targeted person's record directly to
 * `clients/{cid}/persons/{personId}` (a `birth_only` top-up).
 *
 * Unlike `savePersons`, this does NOT derive the id from the form row's INDEX
 * (which would force row 0 → `${cid}_primary`). The caller passes the EXACT
 * target `personId` from the link doc, so a non-primary family member's birth
 * details land on the correct existing person record. The write is `merge:true`
 * (idempotent; never clobbers an email/whatsapp/relationship already on file) —
 * only the supplied birth fields + provenance are set. The same AD-020 CF
 * (`syncPersonToAstroEngine`) mirrors the write to the astro engine.
 */
export async function saveBirthOnlyPerson(
  clientId: string,
  personId: string,
  person: PersonInput,
  via: 'client' | 'staff',
): Promise<void> {
  const dataSource: PersonDataSource =
    via === 'client' ? 'avbp_intake_client' : 'avbp_intake_staff';
  const ref = doc(db, 'clients', clientId, 'persons', personId);
  await setDoc(
    ref,
    {
      ...personDocFromInput(person, dataSource),
      data_source_updated_at: serverTimestamp(),
    },
    { merge: true },
  );
}

/**
 * M5-S5 Flag #1 — record CONSENT PROVENANCE for a `birth_only` top-up.
 *
 * birth_only WAIVES the in-flow consent stage (owner-approved 2026-06-20): a DOB
 * top-up is requested for an EXISTING client who already consented at primary
 * intake, so re-gating is friction with no compliance gain. Rather than leave a
 * dangling/empty consent doc, this stamps an EXPLICIT, AUDITED carry-forward
 * marker on the `BIRTH_ONLY_R1` intake sidecar doc: `consent_source:
 * 'primary_intake'` + who/when/via. Merge-safe; never writes a fake "accepted"
 * acceptance flag-set, so it can never be mistaken for the client ticking the
 * boxes themselves. Report tiers keep their full consent gate (untouched).
 */
export async function saveBirthOnlyConsentCarry(
  scopeOrClientId: IntakeScope | string,
  actor: { uid?: string | null; via: 'client' | 'staff' },
  tier: string = 'BIRTH_ONLY_R1',
): Promise<void> {
  await writeIntakeDoc(asScope(scopeOrClientId), tier, {
    schema_version: INTAKE_SCHEMA_VERSION,
    tier,
    consent_source: 'primary_intake',
    consent_carry_at: serverTimestamp(),
    consent_carry_by: actor.uid || null,
    consent_carry_via: actor.via,
    updated_at: serverTimestamp(),
    updated_by: actor.uid || null,
    updated_via: actor.via,
  });
}

/** The subset of a Person doc the birth_only flow pre-seeds from. */
export interface TargetPerson {
  name?: string;
  relationship?: string;
  dob?: string;
  tob?: string;
  pob?: string;
  lat?: number;
  lon?: number;
  tz?: string;
}

/**
 * M5-S5 — read ONE existing person record (`clients/{cid}/persons/{personId}`)
 * so a birth_only top-up can pre-fill the name + any birth fields already on
 * file. Returns null when the person doc does not exist yet.
 */
export async function loadTargetPerson(
  clientId: string,
  personId: string,
): Promise<TargetPerson | null> {
  const ref = doc(db, 'clients', clientId, 'persons', personId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return (snap.data() as TargetPerson) || null;
}

/**
 * Merge-write the client-owned answers to `clients/{id}/intake/L2B_R1`.
 *
 * CRITICAL — `{ merge: true }` so a later staff hybrid-fill (Slice 2) can add
 * north / boundary / entrance / AUO technical fields to the SAME doc without
 * this write clobbering them, and vice-versa. We also nest the answers under
 * a server-side merge so individual `answers.*` keys are replaced as a block
 * (the form owns the whole client-owned answer set each save — we do NOT do a
 * key-level merge that would orphan a removed concern/person).
 */
export async function saveIntake(
  scopeOrClientId: IntakeScope | string,
  form: IntakeForm,
  actor: { uid?: string | null; via: 'client' | 'staff' },
  tier: string = L2B_R1_TIER,
): Promise<void> {
  const scope = asScope(scopeOrClientId);
  const envelope = buildIntakeEnvelope(form);
  // AD-030: write the project-scoped doc (canonical) AND mirror to the legacy
  // client doc (while the report CF still reads client-scope).
  await writeIntakeDoc(scope, tier, {
    ...envelope,
    updated_at: serverTimestamp(),
    updated_by: actor.uid || null,
    updated_via: actor.via,
  });
  // AD-019: mirror the form persons[] into clients/{id}/persons/* so each person
  // becomes a unified hub record. Persons stay CLIENT-scoped (AD-008/AD-019) —
  // they are reused across projects, so they never move under a project. A CF
  // (AD-020) syncs each to the astro engine.
  await savePersons(scope.clientId, form, actor.via);
  // Astro-subject STEP ③ — intake is a COLLECTOR that also writes the permanent
  // records: upsert each named person as a permanent Contact (clients/{clientNo})
  // and one household Group (families/{id}). Non-fatal: a Contact/Group hiccup
  // must never fail the intake save (the intake doc + person hub are already
  // written above). Errors are logged; the mainline still resolves.
  try {
    await upsertContactsAndGroupFromIntake(scope.clientId, form, actor.via);
  } catch (err) {
    console.warn(
      '[intake] Contact/Group upsert failed (intake + persons already saved):',
      (err as Error).message,
    );
  }
}

/* ══════════════ Astro-subject STEP ③ — intake → Contacts + Group ══════════════
 *
 * On intake save, in addition to the intake doc + the person-hub records, make
 * the PERMANENT records the rest of the app links to:
 *
 *   1. Contact upsert per named person — EACH person (primary + every family
 *      member) becomes/updates a permanent Contact at `clients/{clientNo}`:
 *        • primary (row 0)  → the intake's own `clients/{clientId}` doc already
 *          exists; its personId is `${clientId}_primary`, so clientNo == cid.
 *        • family member    → its own minted `clients/{C#####}`, deduped via a
 *          `contactId` back-reference stamped on the person-hub sub-doc, so a
 *          re-save updates the SAME Contact instead of piling up duplicates.
 *      Fields written (merge): name, dob, tob, geocoded pob {name,lat,lon,tz},
 *      prakriti, relationship (from relation_to_client), health. NO phone —
 *      dependents without their own number simply have no phone (owner decision:
 *      copying the primary's number would cause duplicate messaging).
 *
 *   2. Group upsert — ONE `families` Group for the household, deduped by a stable
 *      `intakeClientKey === clientId`. members[] = {contactId, name, relationship}
 *      for every named person, so the Groups "0 members" becomes real names.
 *      `astroFamilyId` is left null — the astro-link for intake-created Groups is
 *      a LATER refinement (member-overlap reconcile, step ① of the plan).
 *
 * REUSE, not rebuild: the geocoder (Nominatim, same path the People module uses),
 * the client-serial minter (nextClientSeq/clientNoFromSeq), placeOfBirthLabel,
 * and the families collection shape (FamilyMember/FamilyGroup) are all existing.
 * ─────────────────────────────────────────────────────────────────────────── */

/** AV birth-tz default — no-DST policy; the geocoder returns lat/lon only. */
const DEFAULT_POB_TZ = 'Asia/Kolkata';

/**
 * PURE-ish (network geocode only) — build a Contact `pob {name,lat,lon,tz}` from
 * a form person. Prefers the coordinates the People module already geocoded onto
 * the row; when a place is present but coordinates are missing it performs a REAL
 * geocode (never a 0,0 placeholder). Build A (2026-07-06) — the Contact POB now
 * uses `lon` too (was `lng`), so no normalisation is needed: Contact, intake and
 * the astro engine all speak `lon`. Returns undefined when there is neither a
 * usable place nor coordinates (so a merge-write never clobbers an existing pob).
 */
async function pobForContact(
  p: PersonInput,
): Promise<PlaceOfBirth | undefined> {
  const city = (p.pob_city || '').trim();
  const state = (p.pob_state || '').trim();
  const country = (p.pob_country || '').trim();
  let lat = numOrUndef(p.lat);
  let lon = numOrUndef(p.lon);
  const tz = trimOrUndef(p.tz) || DEFAULT_POB_TZ;
  // Real geocode ONLY when we have a place label but no coordinates yet.
  if ((lat === undefined || lon === undefined) && city) {
    const geoCountry = country || 'India';
    const r = await geocode({
      place: city,
      state: state || undefined,
      country: geoCountry,
      country_code: countryCodeHint(geoCountry),
    });
    if (r) {
      lat = r.lat;
      lon = r.lon;
    }
  }
  // Only attach pob when we have REAL coordinates — never a 0,0 lie. (The human
  // place name still lives on the person-hub doc + intake answers regardless.)
  if (lat === undefined || lon === undefined) return undefined;
  // STRUCTURED city/state/country are the source of truth; `name` is a display
  // join (owner fix 2026-07-02) — prefer the structured join, fall back to the
  // explicit free-text label only when no structured parts exist.
  const name = pobDisplayName(city, state, country) || placeOfBirthLabel(p);
  return { name, city, state, country, lat, lon, tz };
}

/**
 * Upsert every named intake person as a permanent Contact and upsert the one
 * household Group. Idempotent: re-saving the same intake updates the SAME Contact
 * and Group rows (dedup: primary → clientId; family → person-doc `contactId`
 * back-ref; Group → `intakeClientKey`).
 */
export async function upsertContactsAndGroupFromIntake(
  clientId: string,
  form: IntakeForm,
  via: 'client' | 'staff',
): Promise<void> {
  const dataSource: PersonDataSource =
    via === 'client' ? 'avbp_intake_client' : 'avbp_intake_staff';
  const named = (form.persons || []).filter((p) => (p.name || '').trim());
  if (named.length === 0) return;

  // 1. Resolve a permanent Contact id for every named person, upsert its person
  //    data, and stage the Group membership rows.
  //
  //    Build C (2026-07-06) — TWO passes so an org-chart edge (`reports_to`) that
  //    names a person added LATER in the intake still resolves: pass 1 resolves
  //    every person's Contact id (and writes its person-data patch) while
  //    recording the intake-row-id → contactId map; pass 2 builds members[]
  //    carrying the durable org-axis (work_role/reports_to/department) + kinship
  //    (relation_to_client), mapping reports_to onto the resolved member
  //    contactId with the self-cycle + stale-ref guards.
  const staged: { contactId: string; person: PersonInput }[] = [];
  const rowIdToContactId = new Map<string, string>();
  for (let i = 0; i < named.length; i += 1) {
    const p = named[i];
    const personId = personIdForRow(clientId, i, p.id);
    // Dedup: primary → the intake's own client doc; family member → a stable
    // Contact minted once and remembered on the person-hub sub-doc.
    let contactId: string;
    if (i === 0) {
      contactId = clientId; // clientNo == cid for the primary (confirmed).
    } else {
      const personRef = doc(db, 'clients', clientId, 'persons', personId);
      const personSnap = await getDoc(personRef);
      const existing =
        personSnap.exists() && typeof personSnap.data().contactId === 'string'
          ? (personSnap.data().contactId as string)
          : '';
      if (existing) {
        contactId = existing;
      } else {
        const seq = await nextClientSeq();
        contactId = clientNoFromSeq(seq);
        // Remember the Contact on the person-hub doc so a re-save dedups to it.
        await setDoc(personRef, { contactId }, { merge: true });
      }
    }

    // Build the Contact person-data patch. Merge-safe: omit absent fields so we
    // never clobber a value set elsewhere (e.g. a phone/email on the card).
    const patch: Record<string, unknown> = {
      name: (p.name || '').trim(),
      source_intake_person_id: personId,
      source_intake_client: clientId,
      updatedAt: serverTimestamp(),
    };
    const dob = trimOrUndef(p.dob);
    if (dob) patch.dob = dob;
    const tob = trimOrUndef(p.time_of_birth);
    if (tob) patch.tob = tob;
    const pob = await pobForContact(p);
    if (pob) patch.pob = pob;
    // NOTE: prakriti is NOT captured per-person at intake (PersonInput has no
    // prakriti field) — it is set later on the Contact "Person data" card. So we
    // deliberately do not write it here; a merge would otherwise clobber a
    // card-set value with undefined. (Flagged for owner: add prakriti to the
    // intake People module if it should be collected at intake.)
    const relToClient = trimOrUndef(p.relation_to_client);
    if (relToClient) patch.relationship = relToClient;
    const health = trimOrUndef(p.health_concerns);
    if (health) patch.health = health;

    // A newly-minted family Contact needs its identity spine seeded ONCE (never
    // overwritten on re-save). NO phone inheritance — a dependent with no number
    // of their own simply has none (owner decision). The primary's own doc
    // already carries its clientNo/contactType, so we don't touch those.
    if (i > 0 && contactId !== clientId) {
      const cSnap = await getDoc(doc(db, 'clients', contactId));
      if (!cSnap.exists()) {
        patch.clientNo = contactId;
        patch.contactType = 'client';
        patch.phones = [];
        patch.emails = [];
        patch.location = '';
        patch.source = via === 'client' ? 'intake_client' : 'intake_staff';
        patch.createdVia = via === 'client' ? 'intake-client' : 'intake-staff';
        patch.createdAt = serverTimestamp();
      }
    }

    await setDoc(doc(db, 'clients', contactId), patch, { merge: true });
    rowIdToContactId.set(p.id, contactId);
    staged.push({ contactId, person: p });
  }

  // 1b. Pass 2 — build the durable member rows carrying the org-axis + kinship.
  //     Preserves the FULL-array-replace contract (the whole members[] is passed
  //     to the group write below, never a delta — familyStore.ts:169).
  const members: FamilyMember[] = staged.map(({ contactId, person }) => {
    const member: FamilyMember = { contactId, name: (person.name || '').trim() };
    const relToClient = trimOrUndef(person.relation_to_client);
    if (relToClient) {
      member.relation_to_client = relToClient;
      // Keep the legacy free-text `relationship` mirror in step (back-compat
      // readers + the existing family-chart heuristic) — unchanged behaviour.
      member.relationship = relToClient;
    }
    const workRole = trimOrUndef(person.work_role);
    if (workRole) member.work_role = workRole;
    const department = trimOrUndef(person.department);
    if (department) member.department = department;
    // reports_to — the intake ROW id of the manager; map it to the manager's
    // resolved member contactId. Guards: drop a STALE ref (the named row is not
    // in this intake) and a DIRECT self-cycle (a member cannot report to itself)
    // — mirrors the IntakeModule directCycle/staleRef guards.
    const rawReports = trimOrUndef(person.reports_to);
    if (rawReports) {
      const managerContactId = rowIdToContactId.get(rawReports);
      if (managerContactId && managerContactId !== contactId) {
        member.reports_to = managerContactId;
      }
    }
    return member;
  });

  // 2. Upsert the ONE household Group, deduped by the stable intakeClientKey.
  //    Read the families collection (small) and find the Group this intake owns.
  const famSnap = await getDocs(collection(db, 'families'));
  let groupId = '';
  for (const d of famSnap.docs) {
    if ((d.data() as { intakeClientKey?: unknown }).intakeClientKey === clientId) {
      groupId = d.id;
      break;
    }
  }
  const groupName = (form.client_name || members[0]?.name || 'Household').trim();
  const isFamily = intendedUseIsFamily(form.intended_use || '');
  const batch = writeBatch(db);
  const now = serverTimestamp();
  if (groupId) {
    // UPDATE in place — refresh members + name; keep astroFamilyId untouched.
    batch.update(doc(db, 'families', groupId), {
      name: groupName,
      members,
      updatedAt: now,
      updatedBy: dataSource,
    });
  } else {
    const ref = doc(collection(db, 'families'));
    groupId = ref.id;
    batch.set(ref, {
      name: groupName,
      type: isFamily ? 'family' : 'organization',
      description: '',
      members,
      intakeClientKey: clientId,
      // Astro-link DEFERRED — reconciled later (member-overlap), never guessed.
      astroFamilyId: null,
      createdAt: now,
      createdBy: dataSource,
      updatedAt: now,
    });
  }
  // Denormalise the Group id onto each member Contact (mirrors familyStore).
  for (const m of members) {
    batch.set(
      doc(db, 'clients', m.contactId),
      { familyIds: arrayUnion(groupId) },
      { merge: true },
    );
  }
  await batch.commit();
}
